import { BKJThemeColorType } from '@bookjane2/bookjane-design-library';

import { ChangeFunctionType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { shiftTypeDetailsMapper } from 'components/ActivityTab/ActivityTab.constants';
import { getActivityColorByStatus } from 'components/ActivityTab/getActivityColorByStatus';
import { createContext, FC, useContext, useMemo, useState } from 'react';
import { getLocalizedDateFormat } from 'utils/getLocalizedDateFormat';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { Event, ActivitiesMetaValue } from 'types/graphql';
import { useTranslation } from 'hooks/useTranslation';
import { isCurrentLanguageFrench } from 'providers/i18NextProvider.constants';
import { DateTime } from 'luxon';
import { UserType } from 'types/activities.types';
import { useSelector } from 'react-redux';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalDataContext';

interface IActivityTabRowProps {
  activityDate: string;
  calloutListId: string;
  dotColor: BKJThemeColorType;
  eventType: string;
  eventName: string;
  hasEdits: boolean;
  hasReason: boolean;
  isAddedToBlock: boolean;
  sentToNrOfAgencies: number;
  showHideDetails: ChangeFunctionType;
  userDetails: UserType;
  hasReasonCategory: boolean;
  reasonCategory: string | null;
  reasonMessage: string | null;
  editDetailList: string[];
  detailsShown: boolean;
  hasDetails: boolean;
  isRowGrey: boolean;
  awardedTo: string;
  timeZone: string | undefined;
}

export const ActivityTabRowPropsContext = createContext<IActivityTabRowProps>(
  {} as IActivityTabRowProps,
);

export const ActivityTabRowPropsProvider: FC<{
  createdAt: string;
  event: Event;
  userDetails: UserType;
  index: number;
  metadata: ActivitiesMetaValue | null;
  timeZone?: string;
}> = ({ children, createdAt, event, userDetails, index, metadata, timeZone }) => {
  const { t, i18n } = useTranslation();
  const { month } = getLocalizedDateFormat(createdAt);
  const [showDetails, setShowDetails] = useState(false);
  const isFrenchFormat = isCurrentLanguageFrench();
  const isUserTimeFormat24h = useSelector(isUserTimeFormat24hSelector);
  const use24HourFormat = isFrenchFormat || isUserTimeFormat24h;
  const atWord = isFrenchFormat ? 'à' : 'at';
  const format = use24HourFormat ? `'${atWord}' HH:mm` : `'at' h:mm a`;
  const todayFormat = use24HourFormat ? 'HH:mm' : 'h:mm a';
  const activityDate = DateTime.fromISO(createdAt)
    .setZone(timeZone)
    .hasSame(DateTime.now().setZone(timeZone), 'day')
    ? DateTime.fromISO(createdAt).setZone(timeZone).toFormat(todayFormat)
    : `${month} ${DateTime.fromISO(createdAt).setZone(timeZone).toFormat(`d, yyyy ${format}`)}`;

  const [calloutListId] = useState(metadata?.calloutListId || '');

  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const isBlockShift = order?.isBlockShift;
  const dotColor = getActivityColorByStatus(event.name, isBlockShift);
  const isReasonOther = metadata?.reasonCategory === 'other';

  let hasEdits = false;
  let hasReason = false;
  let hasReasonCategory = false;
  let sentToNrOfAgencies = -1;
  let detailsShown = false;
  let hasDetails = false;
  let eventValue = event.value;
  let eventName = event.name;
  let awardedTo = '';
  const isAddedToBlock =
    !!metadata?.shiftBlockId &&
    (event.name === 'callout_list_created' || event.name === 'new_callout_list_created');
  let reasonMessage = metadata?.reasonMessage || null;
  let reasonCategory = isReasonOther
    ? ''
    : getTranslatedResponse(metadata?.reasonCategory, 'status', i18n, t) || null;

  switch (event.name) {
    case 'jane_released':
    case 'customer_released':
      hasReason = !!metadata?.reasonMessage || !!metadata?.reasonCategory;
      detailsShown = hasReason;
      hasDetails = hasReason;
      if (reasonMessage && metadata?.reasonCategory && !isReasonOther) {
        reasonCategory = `${reasonCategory} (${t(
          `shift_modal:TIME_OFF_ACTIVITY_${reasonMessage.toUpperCase()}`,
        )})`;
      }
      break;
    case 'cancelled': {
      hasReason = !!metadata?.reasonMessage || !!metadata?.reasonCategory;
      detailsShown = hasReason;
      hasDetails = hasReason;
      break;
    }
    case 'awarded_shift': {
      if (metadata?.reasonMessage) {
        awardedTo = metadata.reasonMessage;
      }
      break;
    }
    case 'updated': {
      hasEdits = !!metadata?.feedableEdits?.length;
      hasReasonCategory = !!metadata?.reasonCategory;
      detailsShown = showDetails;
      hasDetails = hasEdits;
      break;
    }
    case 'agency_assigned': {
      if (!!metadata?.acceptedFor?.name)
        eventValue = `${t('common:ACCEPTED_FOR')} ${metadata?.acceptedFor?.name}`;
      break;
    }
    case 'send_to_agencies':
    case 'sent_to_select_agencies':
    case 'scheduled_send_to_agencies': {
      sentToNrOfAgencies = metadata?.qualifiedAgencyJanesCount || 0;
      break;
    }
  }

  const contextValue = useMemo(
    () => ({
      activityDate,
      calloutListId,
      dotColor,
      eventType: eventValue,
      eventName,
      hasEdits,
      hasReason,
      isAddedToBlock,
      sentToNrOfAgencies,
      showHideDetails: () => setShowDetails(!showDetails),
      reasonMessage,
      userDetails,
      hasReasonCategory,
      reasonCategory,
      editDetailList: (
        metadata?.feedableEdits?.map((edit) =>
          edit.fieldType ? shiftTypeDetailsMapper[edit.fieldType](edit) : '',
        ) || []
      ).filter((shiftEdit) => !!shiftEdit),
      detailsShown,
      hasDetails,
      awardedTo,
      isRowGrey: !!(index % 2),
      timeZone,
    }),
    [
      activityDate,
      calloutListId,
      dotColor,
      eventValue,
      eventName,
      hasEdits,
      hasReason,
      isAddedToBlock,
      sentToNrOfAgencies,
      metadata?.reasonMessage,
      metadata?.reasonCategory,
      metadata?.feedableEdits,
      userDetails,
      hasReasonCategory,
      i18n,
      t,
      detailsShown,
      hasDetails,
      awardedTo,
      index,
      timeZone,
      showDetails,
    ],
  );

  return (
    <ActivityTabRowPropsContext.Provider value={contextValue}>
      {children}
    </ActivityTabRowPropsContext.Provider>
  );
};

export const useActivityTabRowPropsContext = (): IActivityTabRowProps =>
  useContext(ActivityTabRowPropsContext);
